import { useState, useEffect, useCallback } from 'react'
import { getTrackerStandup, getTrackerSummary, syncTracker } from '../services/api'
import TrackerBoard from './TrackerBoard'
import TrackerStandupBoard from './TrackerStandupBoard'
import TrackerPrinters from './TrackerPrinters'
import { fmtAgo, syncStaleness, shouldWarnStale, staleBannerCopy } from './trackerFields'
import { Spinner, useTickingClock } from './trackerShared'

// READ-ONLY Tracker → the open-cases standup board.
// The O3D Tracker Google Sheet is the source of truth. v6: a case is on the
// board when its per-STL Status (col F) says there is work left, and col F also
// decides which lane it sits in. Col CU "Case Status" is ignored — the team
// collapsed it to Done/Active/Cancelled, so it stopped carrying a stage.
// Finished cases and 2025-and-older live in the sheet only.
// NO PATIENT DATA is shown anywhere in the Tracker.

export default function TrackerTab() {
  const [standup, setStandup] = useState({ board: [], summary: null })
  const [summary, setSummary] = useState(null) // lastSync + warnings
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState('')
  const [view, setView] = useState('standup') // 'standup' | 'printers'
  // Board is the default; List is the dense fallback. Remembered per browser so
  // whoever prefers one is not re-choosing every morning.
  const [layout, setLayout] = useState(() => localStorage.getItem('tracker.layout') || 'board')
  const setLayoutPref = (v) => { setLayout(v); localStorage.setItem('tracker.layout', v) }

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [b, s] = await Promise.all([getTrackerStandup(), getTrackerSummary()])
      setStandup(b.data); setSummary(s.data)
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load. Has the first sync run?')
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function syncNow() {
    setSyncing(true); setSyncError('')
    try {
      await syncTracker()
      await load()
    } catch (e) {
      setSyncError(e.response?.status === 409 ? 'A sync is already running — try again in a minute.' : (e.response?.data?.error || 'Sync failed'))
    } finally { setSyncing(false) }
  }

  const sum = standup.summary
  // Has this payload come from a post-split backend? Against a PRE-split one
  // `blocked` still means BOTH groups, so the WORDING has to move with the
  // number — guarding only the values left the Blocked tile reading 42 under a
  // tooltip saying "cases we paused ourselves".
  const split = sum?.awaitingInputs != null

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Tracker <span className="text-xs font-normal text-gray-400 align-middle ml-2 px-2 py-0.5 bg-gray-100 rounded">read-only · sheet is master</span></h2>
          <p className="text-sm text-gray-500 mt-0.5">Open cases, 2026 onward. The per-STL <strong className="font-medium text-gray-600">Status</strong> column (col F) decides the lane — edit it in the sheet, then sync.</p>
        </div>
        <div className="text-right">
          <button onClick={syncNow} disabled={syncing} className="btn-primary text-sm px-4 py-2 whitespace-nowrap disabled:opacity-50 inline-flex items-center gap-2">
            {syncing ? <><Spinner className="h-4 w-4 text-white" /> Syncing…</> : '⟳ Sync now'}
          </button>
          <SyncFreshnessLine at={summary?.lastSync?.at} hasSync={!!summary?.lastSync} loading={loading} error={error} />
        </div>
      </div>

      {syncError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-3">{syncError}</div>}
      <SyncBanner at={summary?.lastSync?.at} loading={loading} error={error} syncing={syncing} onSync={syncNow} />
      {summary?.lastSync?.warnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3 mb-3">
          <span className="font-medium">Last sync had warnings:</span>
          <ul className="list-disc ml-5 mt-1">{summary.lastSync.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {/* sub-tabs: Standup board | Printers fleet */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
        {[{ id: 'standup', label: 'Standup' }, { id: 'printers', label: 'Printers' }].map((v) => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={`text-sm px-3 py-1.5 -mb-px border-b-2 ${view === v.id ? 'border-brand-500 text-brand-600 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {v.label}
          </button>
        ))}
      </div>

      {view === 'standup' && (
        <>
          {/* Summary strip: the numbers worth reading before the board itself.
              Deliberately short — the lane headers already carry per-stage counts,
              so repeating all eight here would be noise. */}
          {sum && (
            <div className="flex flex-wrap items-center gap-2.5 mb-4">
              {/* Open · Active · Awaiting Inputs · Blocked are ADJACENT on
                  purpose — they are the one arithmetic on this strip
                  (Open = Active + Awaiting Inputs + Blocked), and a tile in
                  between breaks the reading. STLs pending counts a different
                  thing (files, not cases) so it follows them.
                  The two exception tiles render even at zero, unlike the ones
                  below: hiding them would leave Open and Active showing the same
                  number with nothing on screen to explain why.
                  `active` and `awaitingInputs` are guarded — an older backend
                  payload has neither key, and before the 2026-09-13 lane split
                  `blocked` meant BOTH groups, so an old payload renders the old
                  two-tile reading rather than a wrong three-tile one.
                  The tooltips state the definition and do not overclaim: an
                  Untriaged or Unknown-status case counts as Active because it is
                  not stopped, which is not the same as confirmed to be moving. */}
              <SummaryStat label="Open cases" value={sum.activeCases} tone="default" />
              {sum.active != null && <SummaryStat label="Active" value={sum.active} tone="blue" title={`Open cases minus ${split ? 'Awaiting Inputs and Blocked' : 'Blocked'}. Untriaged and Unknown-status cases count here too — they are not stopped.`} />}
              {sum.awaitingInputs != null && <SummaryStat label="Awaiting Inputs" value={sum.awaitingInputs} tone={sum.awaitingInputs > 0 ? 'orange' : 'muted'} title="Open cases waiting on someone outside Osteo3d. This is the chase list. A case that is ALSO On Hold counts under Blocked, not here." />}
              <SummaryStat label="Blocked" value={sum.blocked} tone={sum.blocked > 0 ? 'amber' : 'muted'} title={split ? 'Open cases we have paused ourselves — an On Hold / Blocked STL' : 'Open cases with an Awaiting… / On Hold / Blocked STL'} />
              <SummaryStat label="STLs pending" value={sum.pendingStls} tone="default" />
              {sum.overdue > 0 && <SummaryStat label="Overdue" value={sum.overdue} tone="red" />}
              {sum.untriaged > 0 && <SummaryStat label="Untriaged" value={sum.untriaged} tone="muted" title="2026 cases with no Status typed in col F" />}
              {sum.unmapped > 0 && <SummaryStat label="Unknown status" value={sum.unmapped} tone="red" title="Col-F wording the board does not recognise — fix the cell or tell Deepak" />}

              <div className="ml-auto flex items-center rounded-lg border border-gray-200 overflow-hidden">
                {[{ id: 'board', label: 'Board' }, { id: 'list', label: 'List' }].map((o) => (
                  <button
                    key={o.id} onClick={() => setLayoutPref(o.id)}
                    className={`text-xs px-3 py-1.5 ${layout === o.id ? 'bg-brand-50 text-brand-700 font-medium' : 'bg-white text-gray-500 hover:text-gray-700'}`}
                  >{o.label}</button>
                ))}
              </div>
            </div>
          )}
          {/* Lanes need width; below lg the board is unusable, so the list wins
              regardless of the stored preference. */}
          <div className={layout === 'board' ? 'hidden lg:block' : 'hidden'}>
            <TrackerBoard board={standup.board} summary={sum} loading={loading} error={error} />
          </div>
          <div className={layout === 'board' ? 'lg:hidden' : ''}>
            <TrackerStandupBoard board={standup.board} loading={loading} error={error} />
          </div>
        </>
      )}

      {view === 'printers' && <TrackerPrinters />}
    </div>
  )
}

// Tone -> classes, shared by the banner and the line beneath the Sync button so
// the two can never disagree about whether the board is current.
const TONE_BANNER = {
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  red: 'bg-red-50 border-red-200 text-red-800',
}
const TONE_BUTTON = { amber: 'border-amber-300 hover:bg-amber-100', red: 'border-red-300 hover:bg-red-100' }
const TONE_LINE = { amber: 'text-amber-700 font-medium', red: 'text-red-700 font-medium' }

// Both of these own their own clock (useTickingClock) rather than taking `now`
// from TrackerTab — see the hook's comment. They are leaves, so a tick costs a
// few text nodes instead of re-rendering the whole board.

function SyncFreshnessLine({ at, hasSync, loading, error }) {
  const now = useTickingClock()
  const { level } = syncStaleness(at, now)
  const warn = shouldWarnStale({ loading, error, level })
  const copy = warn ? staleBannerCopy(level, fmtAgo(at)) : null
  return (
    <div className={`text-[11px] mt-1 ${(copy && TONE_LINE[copy.tone]) || 'text-gray-400'}`}>
      {hasSync ? `Last synced ${fmtAgo(at)}` : 'Never synced'}
    </div>
  )
}

// A stale board looks exactly like a current one — same lanes, same cards, just
// missing whatever the team typed since the last sync. So say it in the page,
// with the button right there, rather than leaving it to be inferred from a
// timestamp. Silent when fresh: a banner on every visit would be trained out.
function SyncBanner({ at, loading, error, syncing, onSync }) {
  const now = useTickingClock()
  const { level } = syncStaleness(at, now)
  if (!shouldWarnStale({ loading, error, level })) return null
  const copy = staleBannerCopy(level, fmtAgo(at))
  if (!copy) return null
  return (
    <div
      role="status"
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 text-sm rounded-lg px-4 py-3 mb-3 border ${TONE_BANNER[copy.tone]}`}
    >
      <span><span className="font-medium">Board may be behind the sheet.</span> {copy.message}</span>
      <button
        onClick={onSync}
        disabled={syncing}
        className={`ml-auto text-xs px-3 py-1.5 rounded-md border whitespace-nowrap disabled:opacity-50 ${TONE_BUTTON[copy.tone]}`}
      >
        {syncing ? 'Syncing…' : '⟳ Sync now'}
      </button>
    </div>
  )
}

const TONES = {
  default: 'bg-white border-gray-200 text-gray-800',
  muted: 'bg-gray-50 border-gray-200 text-gray-400',
  red: 'bg-red-50 border-red-200 text-red-700',
  green: 'bg-green-50 border-green-200 text-green-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  // Matches the Awaiting Inputs lane accent, so the tile and the board column
  // read as the same thing. Amber stays Blocked's.
  orange: 'bg-orange-50 border-orange-200 text-orange-700',
  blue: 'bg-blue-50 border-blue-200 text-blue-700',
}
function SummaryStat({ label, value, tone = 'default', title }) {
  return (
    <div className={`rounded-lg border px-3.5 py-2 min-w-[110px] ${TONES[tone] || TONES.default}`} title={title}>
      <div className="text-xl font-semibold leading-none tabular-nums">{(value ?? 0).toLocaleString()}</div>
      <div className="text-xs mt-1 opacity-80">{label}</div>
    </div>
  )
}
